import { apiClient, apiClientPaginated, ApiError } from '@/lib/api-client';
import type {
  CreateSupplierPayload,
  UpdateSupplierPayload,
  Supplier,
  SupplierListParams,
  SupplierListResponse,
  SupplierStats,
  SupplierPOListResponse,
} from './types';

async function withFallback<T>(fn: () => Promise<T>, fallback: T): Promise<T> {
  try {
    return await fn();
  } catch (err) {
    if (err instanceof ApiError && (err.statusCode === 404 || err.statusCode === 501)) {
      return fallback;
    }
    throw err;
  }
}

function buildQuery(params: Record<string, string | number | boolean | undefined>): string {
  const entries = Object.entries(params).filter(([, v]) => v !== undefined && v !== '');
  if (entries.length === 0) return '';
  return `?${entries.map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`).join('&')}`;
}

const EMPTY_LIST: SupplierListResponse = {
  data: [],
  meta: { page: 1, limit: 20, total: 0, totalPages: 0 },
};

const EMPTY_STATS: SupplierStats = { total: 0, active: 0, with_gstin: 0, new_this_month: 0 };

export const suppliersApi = {
  list: (params: SupplierListParams) =>
    withFallback(
      () =>
        apiClientPaginated<SupplierListResponse['data']>(
          `/suppliers${buildQuery({
            page: params.page,
            limit: params.limit,
            search: params.search,
            is_active: params.is_active !== undefined ? String(params.is_active) : undefined,
          })}`,
        ) as Promise<SupplierListResponse>,
      EMPTY_LIST,
    ),

  getById: (id: string) =>
    withFallback(() => apiClient<Supplier>(`/suppliers/${id}`), null),

  create: (payload: CreateSupplierPayload) =>
    apiClient<Supplier>('/suppliers', {
      method: 'POST',
      body: payload,
    }),

  update: (id: string, payload: UpdateSupplierPayload) =>
    apiClient<Supplier>(`/suppliers/${id}`, {
      method: 'PUT',
      body: payload,
    }),

  deactivate: (id: string, reason?: string) =>
    apiClient<Supplier>(`/suppliers/${id}/deactivate`, {
      method: 'PATCH',
      body: reason ? { reason } : {},
    }),

  reactivate: (id: string) =>
    apiClient<Supplier>(`/suppliers/${id}/reactivate`, {
      method: 'PATCH',
      body: {},
    }),

  getStats: () =>
    withFallback(() => apiClient<SupplierStats>('/suppliers/stats'), EMPTY_STATS),

  listPurchaseOrders: (id: string, page = 1, limit = 10) =>
    withFallback(
      () =>
        apiClientPaginated<SupplierPOListResponse['data']>(
          `/suppliers/${id}/purchase-orders${buildQuery({ page, limit })}`,
        ) as Promise<SupplierPOListResponse>,
      { data: [], meta: { page: 1, limit: 10, total: 0, totalPages: 0 } },
    ),
};
